import { z } from "zod";
import { Accession, Cik, IsoDate } from "./primitives.js";
/**
 * @fileoverview Company financial history, from the SEC's `companyfacts` XBRL API.
 *
 * It holds only facts reported without dimensional qualification — the consolidated
 * entity. Segment breakouts are absent, so this gives the parent's consolidated history
 * and, once SpinCo has filed its own annual report, SpinCo's standalone history, but not
 * SpinCo's revenue as a segment of the parent.
 */
/**
 * One reported observation of one concept for one period, by one filing.
 *
 * The same period is reported many times — once when it happens, and again in every
 * later filing that shows it as a comparative. Those are kept, not collapsed: on a
 * separation, a period restated on a post-separation basis is often the finding.
 */
export const FactObservation = z.object({
    /** Null for an instant fact — a balance-sheet figure has an "as of", not a span. */
    periodStart: IsoDate.nullable(),
    periodEnd: IsoDate,
    /**
     * A decimal string, never a number: `USD/shares` concepts are fractional, and money is
     * never a float.
     */
    value: z.string().regex(/^-?\d+(\.\d+)?$/, "Value must be a plain decimal string"),
    unit: z.string().min(1),
    /**
     * The filing that reported it. Not a foreign key to `filings`: the filing need not be
     * in Mosaic's corpus, and it still resolves on EDGAR.
     */
    accession: Accession,
    form: z.string().min(1),
    filedOn: IsoDate,
    /**
     * SEC's own calendar-period normalisation, present only on the one fact it picked
     * as canonical for that period.
     */
    frame: z.string().nullable(),
});
/**
 * How a fact's period was classified — derived from its dates, never from the filing's
 * own `fy`/`fp` fields.
 *
 * Those fields describe the filing, not the fact: a calendar-2024 figure in a 10-K filed
 * in fiscal 2025 is tagged `fy: 2025`.
 */
export const PeriodBasis = z.enum(["annual", "quarterly", "ytd", "instant", "other"]);
/** One period of one concept, with every vintage that reported it. */
export const SeriesPoint = z.object({
    periodStart: IsoDate.nullable(),
    periodEnd: IsoDate,
    basis: PeriodBasis,
    /** The earliest filing to report this period — the figure as it was first told. */
    asFirstReported: FactObservation,
    /** The most recent filing to report it. Equal to `asFirstReported` when never restated. */
    latest: FactObservation,
    /**
     * True when a later filing reported a different value for the same period.
     */
    wasRestated: z.boolean(),
    /** Every vintage, newest first, so a restatement can be read rather than inferred. */
    observations: z.array(FactObservation),
});
/** One company's history of one XBRL concept, in one unit and on one period basis. */
export const ConceptSeries = z.object({
    cik: Cik,
    entityName: z.string(),
    taxonomy: z.string(),
    /**
     * The XBRL tag actually used, since one standard concept maps to several tags.
     */
    concept: z.string(),
    label: z.string().nullable(),
    unit: z.string(),
    basis: PeriodBasis,
    points: z.array(SeriesPoint),
});
/**
 * The concepts a research question asks for, each resolving through `CONCEPT_TAGS` to a
 * ranked list of XBRL tags, because tag choice varies by filer and by era.
 */
export const StandardConcept = z.enum([
    "revenue",
    "gross_profit",
    "operating_income",
    "net_income",
    "eps_basic",
    "eps_diluted",
    "assets",
    "liabilities",
    "equity",
    "cash",
    "operating_cash_flow",
    "capex",
    "long_term_debt",
    "shares_outstanding",
]);
/** The XBRL tags each standard concept resolves to, most preferred first. */
export const CONCEPT_TAGS = Object.freeze({
    revenue: [
        "RevenueFromContractWithCustomerExcludingAssessedTax",
        "RevenueFromContractWithCustomerIncludingAssessedTax",
        "Revenues",
        "SalesRevenueNet",
        "SalesRevenueGoodsNet",
    ],
    gross_profit: ["GrossProfit"],
    operating_income: ["OperatingIncomeLoss"],
    net_income: [
        "NetIncomeLoss",
        "ProfitLoss",
        "NetIncomeLossAvailableToCommonStockholdersBasic",
    ],
    eps_basic: ["EarningsPerShareBasic"],
    eps_diluted: ["EarningsPerShareDiluted"],
    assets: ["Assets"],
    liabilities: ["Liabilities"],
    equity: [
        "StockholdersEquity",
        "StockholdersEquityIncludingPortionAttributableToNoncontrollingInterest",
    ],
    cash: [
        "CashAndCashEquivalentsAtCarryingValue",
        "CashCashEquivalentsRestrictedCashAndRestrictedCashEquivalents",
    ],
    operating_cash_flow: [
        "NetCashProvidedByUsedInOperatingActivities",
        "NetCashProvidedByUsedInOperatingActivitiesContinuingOperations",
    ],
    capex: [
        "PaymentsToAcquirePropertyPlantAndEquipment",
        "PaymentsToAcquireProductiveAssets",
    ],
    long_term_debt: [
        "LongTermDebtNoncurrent",
        "LongTermDebt",
        "DebtInstrumentCarryingAmount",
    ],
    shares_outstanding: [
        "CommonStockSharesOutstanding",
        "EntityCommonStockSharesOutstanding",
        "WeightedAverageNumberOfSharesOutstandingBasic",
    ],
});
/** The unit each standard concept is expected in. A tag can be reported in several. */
export const CONCEPT_UNITS = Object.freeze({
    revenue: "USD",
    gross_profit: "USD",
    operating_income: "USD",
    net_income: "USD",
    eps_basic: "USD/shares",
    eps_diluted: "USD/shares",
    assets: "USD",
    liabilities: "USD",
    equity: "USD",
    cash: "USD",
    operating_cash_flow: "USD",
    capex: "USD",
    long_term_debt: "USD",
    shares_outstanding: "shares",
});
/** One concept a company has tagged, so a caller can see what exists before asking. */
export const AvailableConcept = z.object({
    taxonomy: z.string(),
    concept: z.string(),
    unit: z.string(),
    observations: z.number().int().nonnegative(),
    earliestPeriod: IsoDate,
    latestPeriod: IsoDate,
});
/** A named peer's series alongside the subject's, for the comparison question. */
export const ComparisonRow = z.object({
    cik: Cik,
    entityName: z.string(),
    /** Null when the company has never tagged any of the concept's candidate tags. */
    series: ConceptSeries.nullable(),
    /** Why it is null, when it is — an unfetched company is not the same as an untagged one. */
    unavailable: z.string().nullable(),
});
//# sourceMappingURL=financials.js.map